import * as React from 'react';
import { cn } from '@lib/utils';
import { FC, ReactNode } from 'react';

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'bg-card text-card-foreground flex flex-col rounded-[10px] p-5',
        'border dark:border-transparent',
        className
      )}
      data-slot="card"
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-1.5 px-6', className)}
      data-slot="card-header"
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('leading-none font-semibold', className)}
      data-slot="card-title"
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="card-description"
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('px-6', className)}
      data-slot="card-content"
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-center px-6', className)}
      data-slot="card-footer"
      {...props}
    />
  );
}

interface CardStatProps {
  icon: ReactNode;
  value: string;
  desc: string;
  className?: string;
  onClick?: () => void;
}
const CardStat: FC<CardStatProps> = ({
  className,
  icon,
  value,
  desc,
  onClick,
}) => {
  return (
    <div
      className={cn(
        'flex flex-1 flex-col bg-card-stat p-5 min-w-[230px] rounded-[15px]',
        className
      )}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className={'flex justify-between gap-x-2'}>{icon}</div>
      <p className={'text-[32px] font-medium mt-4.5 mb-4'}>{value}</p>
      <p className={'text-sm text-[#8B8B8B]'}>{desc}</p>
    </div>
  );
};

interface CardRankingProps {
  bgColor: string;
  opacity?: number;
  className?: string;
}
const CardRanking: FC<CardRankingProps & React.ComponentProps<'div'>> = ({
  bgColor,
  opacity = 0.6,
  className,
  ...props
}) => {
  return (
    <div className={'relative'}>
      <div
        className={`opacity-60 absolute h-full w-full`}
        style={{ backgroundColor: bgColor, opacity }}
      />
      <div className={cn('relative', className)} {...props} />
    </div>
  );
};

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardStat,
  CardRanking,
};
